import emailjs from "@emailjs/browser";
import { useState } from "react";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaRegCommentDots,
  FaUser,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import RenderToast from "../RenderToast";
import styles from "./ContactForm.module.css";

const initialValues = {
  name: "",
  email: "",
  phone: "",
  message: "",
  consent: false,
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emailJsConfig = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
};

export default function ContactForm() {
  const [formValues, setFormValues] = useState(initialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = ({ target }) => {
    const { name, value, type, checked } = target;

    setFormValues((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const values = {
      name: formValues.name.trim(),
      email: formValues.email.trim(),
      phone: formValues.phone.trim(),
      message: formValues.message.trim(),
      consent: formValues.consent,
    };

    if (!values.name) {
      RenderToast({
        type: "error",
        message: "Please enter your name. It is required.",
      });
      return;
    }

    if (!values.email) {
      RenderToast({
        type: "error",
        message: "Please enter your email. It is required.",
      });
      return;
    }

    if (!emailPattern.test(values.email)) {
      RenderToast({
        type: "error",
        message: "Please enter a valid email address.",
      });
      return;
    }

    if (!values.phone) {
      RenderToast({
        type: "error",
        message: "Please enter your phone number. It is required.",
      });
      return;
    }

    if (!values.consent) {
      RenderToast({
        type: "error",
        message: "Please accept the consent checkbox to continue.",
      });
      return;
    }

    const { serviceId, templateId, publicKey } = emailJsConfig;

    if (!serviceId || !templateId || !publicKey) {
      RenderToast({
        type: "error",
        message:
          "Email service is not configured yet. Add the EmailJS keys to your .env file.",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      await emailjs.send(
        serviceId,
        templateId,
        {
          from_name: values.name,
          from_email: values.email,
          phone: values.phone,
          message: values.message || "No message provided.",
          consent: "Yes",
        },
        { publicKey },
      );

      RenderToast({
        type: "success",
        message:
          "Your message has been submitted successfully. Our team will reach out soon.",
      });

      setFormValues(initialValues);
    } catch {
      RenderToast({
        type: "error",
        message:
          "We could not send your message right now. Please try again in a moment.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
      <div className={styles.fieldGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Name *</span>

          <div className={styles.inputWrap}>
            <span className={styles.icon} aria-hidden="true">
              <FaUser />
            </span>

            <input
              type="text"
              name="name"
              value={formValues.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              autoComplete="name"
            />
          </div>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Email *</span>

          <div className={styles.inputWrap}>
            <span className={styles.icon} aria-hidden="true">
              <FaEnvelope />
            </span>

            <input
              type="email"
              name="email"
              value={formValues.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              autoComplete="email"
            />
          </div>
        </label>

        <label className={`${styles.field} ${styles.fieldPhone}`}>
          <span className={styles.fieldLabel}>Phone *</span>

          <div className={styles.inputWrap}>
            <span className={styles.icon} aria-hidden="true">
              <FaPhoneAlt />
            </span>

            <input
              type="tel"
              name="phone"
              value={formValues.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
              autoComplete="tel"
            />
          </div>
        </label>

        <label className={`${styles.field} ${styles.fieldFull}`}>
          <span className={styles.fieldLabel}>Message</span>

          <div className={`${styles.inputWrap} ${styles.textareaWrap}`}>
            <span className={styles.icon} aria-hidden="true">
              <FaRegCommentDots />
            </span>

            <textarea
              name="message"
              value={formValues.message}
              onChange={handleChange}
              placeholder="Tell us about your organization, current challenges, or goals."
              rows={3}
            />
          </div>
        </label>
      </div>

      <label htmlFor="contact-consent" className={styles.consent}>
        <input
          id="contact-consent"
          type="checkbox"
          name="consent"
          checked={formValues.consent}
          onChange={handleChange}
        />

        <span className={styles.consentText}>
          I agree to <strong>MITSI</strong>{" "}
          <Link to="/terms-and-conditions/">Terms &amp; Conditions.</Link>
        </span>
      </label>

      <button
        type="submit"
        className={styles.submitButton}
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending..." : "Send Message"}
      </button>
    </form>
  );
}
